'use client';

import React, { RefObject } from 'react';
import { useScrollTrigger } from '@mui/material';
import { useCleanPathname } from '@ianesthesie-kit/core';
import { useAppSelector } from '@/hooks';
import { selectLastOpenedPatient } from '@/modules/ui';
import { FloatingAction, FloatingActionsStack } from './FloatingActionsStack';
import { ScrollTopFab } from './ScrollTopFab';
import { LastOpenedPatientFab } from './LastOpenedPatientFab';

type FloatingActionsProps = {
  scrollTopAnchorRef: RefObject<HTMLElement | null>;
};

/** Decides which global floating buttons show right now and stacks them bottom-right. */
export const FloatingActions = ({
  scrollTopAnchorRef
}: FloatingActionsProps) => {
  const showScrollTop = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100
  });
  const lastOpenedPatient = useLastOpenedPatientToShow();

  return (
    <FloatingActionsStack>
      {showScrollTop && (
        <FloatingAction key="scroll-top">
          <ScrollTopFab anchorRef={scrollTopAnchorRef} />
        </FloatingAction>
      )}
      {lastOpenedPatient && (
        <FloatingAction key={`last-patient-${lastOpenedPatient.id}`} nudge>
          <LastOpenedPatientFab patient={lastOpenedPatient} />
        </FloatingAction>
      )}
    </FloatingActionsStack>
  );
};

/** The last opened patient, unless the user is already on that dossier. */
const useLastOpenedPatientToShow = () => {
  const lastOpenedPatient = useAppSelector(selectLastOpenedPatient);
  const [section, patientId] = useCleanPathname().split('/').filter(Boolean);
  const onThatDossier =
    section === 'patients' && patientId === lastOpenedPatient?.id;

  return onThatDossier ? undefined : lastOpenedPatient;
};
